import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import {
  amountFormatter,
  ManagedProductCategory,
  retrieveColumnsForFinancialPositions,
  usePirBoxData,
  walletFinancialPositionsMessages,
} from 'azshared';
import React, { Fragment, MouseEvent, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { AppTableCell } from '../../../../../components/table/table-cell';
import { AppTableCellHeader } from '../../../../../components/table/table-cell-header';
import './managed-table.scss';

interface ManagedTableProps {
  productCategory: ManagedProductCategory;
  onSelectedProduct: (e: MouseEvent<HTMLSpanElement> | React.KeyboardEvent<HTMLSpanElement>) => void;
}

export const ManagedTable = (props: ManagedTableProps) => {
  const { productCategory, onSelectedProduct } = props;

  const { formatMessage } = useIntl();

  const columns = useMemo(() => retrieveColumnsForFinancialPositions('Managed', productCategory.type), [productCategory.type]);

  const { hasAlternative, hasOrdinary } = usePirBoxData();
  const hasBothPir = hasAlternative && hasOrdinary;

  const flagShowDisclaimer = useMemo(
    () => productCategory?.macroProducts.flatMap((macroProduct) => macroProduct.products)?.some((product) => product.flagSGR),
    [productCategory]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLSpanElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onSelectedProduct(event);
      }
    },
    [onSelectedProduct]
  );

  const getPaperContractCode = useCallback((v: string) => {
    const gruppi = v?.split('-');
    if (gruppi?.length > 3) {
      return gruppi.slice(3).join('-');
    }
    return v;
  }, []);

  return (
    <Fragment>
      <TableContainer className='managed-table'>
        <Table aria-label='table'>
          <TableHead className='managed-table-head'>
            <TableRow>
              {columns.map((column, index) => (
                <AppTableCellHeader key={index} className={`managed-table-cell-header-${column.key}`}>
                  {formatMessage(column.headerLabel)}
                </AppTableCellHeader>
              ))}
            </TableRow>
          </TableHead>
          <TableBody className='managed-table-body'>
            {productCategory.macroProducts.map((macroProduct) => (
              <Fragment key={macroProduct.name}>
                <TableRow className='managed-table-row managed-table-row-group-name'>
                  <AppTableCell align='left' type={'string'}>
                    {macroProduct.name}
                  </AppTableCell>
                  <AppTableCell type={'string'} colSpan={columns.length - 1} />
                </TableRow>
                {macroProduct.products.map((product: any, index) => (
                  <TableRow key={index} className='managed-table-row'>
                    {columns.map((col) => (
                      <Fragment key={col.key}>
                        {col.key === 'productName' && (
                          <AppTableCell key={col.key} align='left' type={col.type}>
                            <span
                              id={product.productCode}
                              className='managed-table-row-cell-product-name'
                              onClick={onSelectedProduct}
                              onKeyDown={handleKeyDown}
                              tabIndex={0}>
                              {product[col.key]} {product.flagSGR && '(1)'}{' '}
                              {product.pirType && (hasBothPir ? (product.pirType === 'Alternative' ? '*' : '****') : '*')}
                            </span>
                          </AppTableCell>
                        )}
                        {col.key === 'contract' && (
                          <AppTableCell key={col.key} align='left' type={col.type}>
                            {getPaperContractCode(product[col.key])}
                          </AppTableCell>
                        )}
                        {col.key !== 'productName' && col.key !== 'contract' && (
                          <AppTableCell key={col.key} align='left' type={col.type}>
                            {product[col.key]}
                          </AppTableCell>
                        )}
                      </Fragment>
                    ))}
                  </TableRow>
                ))}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <div className='managed-table-footer'>
        <p className='managed-table-footer-total-amount'>
          <span>{formatMessage(walletFinancialPositionsMessages.footerTotalLabel)}</span>
          {amountFormatter(productCategory?.totalAmount, { currencyDisplay: 'code' })}
        </p>
        {flagShowDisclaimer && (
          <p className='managed-table-footer-disclaimer'>{formatMessage(walletFinancialPositionsMessages.managedCategoryFooterDisclaimer)}</p>
        )}
      </div>
    </Fragment>
  );
};
